import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';

import { Box, Spinner, Center, Text } from '@chakra-ui/react';

import ListingHeader from './components/ListingHeader';
import TabNavigation from './components/TabNavigation';
import GeneralTab from './components/GeneralTab';
import AdvancedTab from './components/AdvancedTab';
import OptimizerTab from './components/OptimizerTab';

import { getListingApi, updateListingApi } from 'features/listings/listings.api';

const Listing = () => {
  const { id } = useParams();
  const dispatch = useDispatch();
  const { currentListing, getListingByIdStatus, updateListingStatus, error } =
    useSelector((state) => state.listings);

  const [activeTab, setActiveTab] = useState('general');
  const [images, setImages] = useState([]);
  const [errorImages, setErrorImages] = useState([]);

  // State for tracking changes
  const [changedFields, setChangedFields] = useState({});
  const originalListing = useRef(null);

  useEffect(() => {
    if (id) {
      dispatch(getListingApi(id));
    }
  }, [dispatch, id]);

  useEffect(() => {
    if (currentListing) {
      // Store the original listing data for comparison
      originalListing.current = {
        title: currentListing.details?.title || '',
        sku: currentListing.sku || '',
        price: currentListing.details?.price || '',
        quantity: currentListing.details?.quantity || '',
        packageWeight: currentListing.marketplaceDetails?.packageWeight || {},
        packageDimensions: currentListing.marketplaceDetails?.packageDimensions || {},
        itemSpecifics: currentListing.marketplaceDetails?.itemSpecifics || {},
        description: currentListing.marketplaceDetails?.description || '',
      };

      // Reset changed fields when new listing is loaded
      setChangedFields({});

      if (currentListing.marketplaceDetails?.images) {
        const processedImages = currentListing.marketplaceDetails.images.map(
          (url, index) => ({
            id: index + 1,
            url,
            isMain: index === 0,
            isValid: true,
            width: 1600,
            height: 1200,
            isNew: false,
          }),
        );
        setImages(processedImages);
      }
    }
  }, [currentListing]);

  // Get max images based on marketplace
  const getMaxImages = () => {
    if (!currentListing) return 9;
    return currentListing.marketplace?.name?.toLowerCase() === 'amazon' ? 9 : 24;
  };

  // Handle listing updates with change tracking
  const handleListingUpdate = (updates) => {
    setChangedFields((prev) => ({
      ...prev,
      ...updates,
    }));
  };

  // Handle images update
  const handleImagesUpdate = (updatedImagesOrFunction) => {
    // Handle both direct arrays and state updater functions
    let updatedImages;
    if (typeof updatedImagesOrFunction === 'function') {
      updatedImages = updatedImagesOrFunction(images);
      setImages(updatedImages);
    } else {
      updatedImages = updatedImagesOrFunction;
      setImages(updatedImages);
    }

    // Check if images have changed
    const originalImageUrls = currentListing.marketplaceDetails?.images || [];
    const currentImageUrls = updatedImages
      .filter((img) => !img.isNew)
      .map((img) => img.url);

    const newImages = updatedImages.filter((img) => img.isNew);

    // Check for changes: different count, different URLs, new images, or different order
    const imagesChanged =
      originalImageUrls.length !== currentImageUrls.length ||
      !originalImageUrls.every((url) => currentImageUrls.includes(url)) ||
      newImages.length > 0 ||
      // Check if order has changed by comparing arrays element by element
      (originalImageUrls.length === currentImageUrls.length &&
        !originalImageUrls.every((url, index) => url === currentImageUrls[index]));

    if (imagesChanged) {
      // Format images for API
      const formattedImages = updatedImages.map((img) => ({
        url: img.url,
        data: img.data, // base64 data for new images
        name: img.name,
        isNew: img.isNew || false,
      }));

      setChangedFields((prev) => ({
        ...prev,
        images: formattedImages,
      }));
    } else {
      // If no image changes detected, remove images from changed fields
      setChangedFields((prev) => {
        const { images: removedImages, ...rest } = prev;
        return rest;
      });
    }
  };

  // Handle update action
  const handleUpdate = async () => {
    if (Object.keys(changedFields).length === 0) {
      console.log('No changes to update');
      return;
    }

    console.log('Updating listing with changes:', changedFields);

    try {
      await dispatch(
        updateListingApi({
          id: currentListing._id,
          updateData: changedFields,
        }),
      ).unwrap();

      // Reset changed fields after successful update
      setChangedFields({});

      // Reload the listing to get updated data
      dispatch(getListingApi(id));
    } catch (error) {
      console.error('Update failed:', error);
    }
  };

  // Handle delete action
  const handleDelete = () => {
    console.log('Deleting listing...', currentListing.externalPlatformId);
    // Add your delete logic here
  };

  if (getListingByIdStatus === 'pending') {
    return (
      <Center minH="50vh">
        <Spinner size="xl" />
      </Center>
    );
  }

  if (getListingByIdStatus === 'rejected' || !currentListing) {
    return (
      <Center minH="50vh">
        <Text color="red.500" fontSize="lg">
          {error || 'Failed to load listing'}
        </Text>
      </Center>
    );
  }

  // Tab configuration
  const tabs = [
    { id: 'general', label: 'General' },
    { id: 'advanced', label: 'Advanced' },
    { id: 'optimizer', label: 'Optimizer' },
  ];

  return (
    <Box px={{ base: 4, md: 10 }} py={{ base: 6, md: 8 }} mt="50px">
      {/* Header Section */}
      <ListingHeader listing={currentListing} />

      {/* Tab Navigation */}
      <TabNavigation activeTab={activeTab} setActiveTab={setActiveTab} tabs={tabs} />

      {/* Tab Content */}
      {activeTab === 'general' && (
        <GeneralTab
          images={images}
          setImages={handleImagesUpdate}
          errorImages={errorImages}
          setErrorImages={setErrorImages}
          getMaxImages={getMaxImages}
          listing={currentListing}
          onListingUpdate={handleListingUpdate}
          onUpdate={handleUpdate}
          onDelete={handleDelete}
          isUpdating={updateListingStatus === 'pending'}
          hasChanges={Object.keys(changedFields).length > 0}
        />
      )}

      {activeTab === 'advanced' && (
        <AdvancedTab
          listing={currentListing}
          onListingUpdate={handleListingUpdate}
          onUpdate={handleUpdate}
          isUpdating={updateListingStatus === 'pending'}
          hasChanges={Object.keys(changedFields).length > 0}
        />
      )}

      {activeTab === 'optimizer' && <OptimizerTab listing={currentListing} />}
    </Box>
  );
};

export default Listing;
